// src/components/modals/ConfirmationDialog.jsx
import { useState } from 'react';
import { AlertTriangle, Trash2, X } from 'lucide-react';
import { Button } from '../ui/Button/Button';
import { Textarea } from '../ui/Textarea';

export function ConfirmationDialog({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  confirmText = 'Confirm', 
  cancelText = 'Cancel',
  variant = 'danger', // 'danger', 'warning', 'info'
  requireReason = false,
  isLoading = false
}) {
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (requireReason && !reason.trim()) {
      setReasonError('Please provide a reason for this action');
      return;
    }
    
    onConfirm(reason.trim());
    setReason('');
    setReasonError('');
  };

  const handleClose = () => {
    setReason('');
    setReasonError('');
    onClose();
  };

  const getVariantStyles = () => {
    switch (variant) {
      case 'danger':
        return {
          icon: Trash2,
          iconColor: 'text-red-600',
          iconBg: 'bg-red-100',
          confirmButton: 'bg-red-600 hover:bg-red-700 text-white'
        };
      case 'warning':
        return {
          icon: AlertTriangle,
          iconColor: 'text-yellow-600',
          iconBg: 'bg-yellow-100',
          confirmButton: 'bg-yellow-600 hover:bg-yellow-700 text-white'
        };
      default:
        return {
          icon: AlertTriangle,
          iconColor: 'text-blue-600',
          iconBg: 'bg-blue-100',
          confirmButton: 'bg-blue-600 hover:bg-blue-700 text-white'
        };
    }
  };

  const styles = getVariantStyles();
  const Icon = styles.icon;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${styles.iconBg}`}>
              <Icon size={20} className={styles.iconColor} />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-600 mb-4">{message}</p>
          
          {requireReason && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Reason for this action *
              </label>
              <Textarea
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  if (reasonError) setReasonError('');
                }}
                placeholder="Please provide a reason..."
                rows={3}
                error={reasonError}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            {cancelText}
          </Button>
          
          <Button
            onClick={handleConfirm}
            disabled={isLoading}
            className={styles.confirmButton}
          >
            {isLoading ? 'Processing...' : confirmText}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Specialized confirmation dialogs for common use cases
export function DeleteDriverDialog({ isOpen, onClose, onConfirm, driverName, isLoading }) {
  return (
    <ConfirmationDialog
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      title="Delete Driver"
      message={`Are you sure you want to delete ${driverName}? This action will mark the driver as deleted and they will no longer be able to access the platform.`}
      confirmText="Delete Driver"
      variant="danger"
      requireReason={true}
      isLoading={isLoading}
    />
  );
}

export function ApproveDriverDialog({ isOpen, onClose, onConfirm, driverName, isLoading }) {
  return (
    <ConfirmationDialog
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      title="Approve Driver"
      message={`Are you sure you want to approve ${driverName}? This will grant them access to the platform and allow them to start receiving trips.`}
      confirmText="Approve Driver"
      variant="info"
      isLoading={isLoading}
    />
  );
}

export function RejectDriverDialog({ isOpen, onClose, onConfirm, driverName, isLoading }) {
  return (
    <ConfirmationDialog
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      title="Reject Driver"
      message={`Are you sure you want to reject ${driverName}'s application? This action cannot be undone and they will need to reapply.`}
      confirmText="Reject Application"
      variant="danger"
      requireReason={true}
      isLoading={isLoading}
    />
  );
}

export function SuspendDriverDialog({ isOpen, onClose, onConfirm, driverName, isLoading }) {
  return (
    <ConfirmationDialog
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      title="Suspend Driver"
      message={`Are you sure you want to suspend ${driverName}? They will be temporarily unable to receive trips until the suspension is lifted.`}
      confirmText="Suspend Driver"
      variant="warning"
      requireReason={true}
      isLoading={isLoading}
    />
  );
}

export function RestoreDriverDialog({ isOpen, onClose, onConfirm, driverName, isLoading }) {
  return (
    <ConfirmationDialog
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      title="Restore Driver"
      message={`Are you sure you want to restore ${driverName}? This will reactivate their account and allow them to access the platform again.`}
      confirmText="Restore Driver"
      variant="info"
      isLoading={isLoading}
    />
  );
}
